import React, { useEffect, useState, useRef } from "react";
import ImprovedDatePicker from "./Form/ImprovedDatePicker";
import {
  emptyPatientForm,
  formatDateForInput,
  formatPhoneNumber,
  PatientFormData,
  validateCity,
  validateDateOfBirth,
  validateName,
  validatePhoneNumber,
  validateSsnLast4,
  validateState,
  validateZip,
} from "../utils/patientValidation";

interface PatientFormModalProps {
  isOpen: boolean;
  title: string;
  subtitle: string;
  primaryLabel: string;
  loadingLabel: string;
  onClose: () => void;
  onSubmit: (data: PatientFormData) => Promise<void>;
  validateAddress: (address: string) => string;
  initialData?: Partial<PatientFormData>;
  buildConflictMessage?: (data: PatientFormData, error: any) => string | null;
  defaultErrorMessage: string;
}

const PatientFormModal: React.FC<PatientFormModalProps> = ({
  isOpen,
  title,
  subtitle,
  primaryLabel,
  loadingLabel,
  onClose,
  onSubmit,
  validateAddress,
  initialData = {},
  buildConflictMessage,
  defaultErrorMessage,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PatientFormData>(emptyPatientForm);
  const [validationErrors, setValidationErrors] = useState<
    Record<keyof PatientFormData, string>
  >({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
    ssnLast4: "",
    address: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
  });
  const prevIsOpenRef = useRef(false); // Initialize to false to catch mount with isOpen=true
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Initialize form when modal opens (transitions from closed to open) or on mount if already open
    // This prevents the form from being reset while user is typing
    if (isOpen && (!prevIsOpenRef.current || !hasInitializedRef.current)) {
      // Use current initialData prop directly to ensure we have the latest value
      const formatted = {
        ...emptyPatientForm,
        ...initialData,
        dateOfBirth: initialData.dateOfBirth
          ? formatDateForInput(initialData.dateOfBirth)
          : "",
      };
      setFormData(formatted as PatientFormData);
      setValidationErrors({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        dateOfBirth: "",
        ssnLast4: "",
        address: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: "",
      });
      setError(null);
      hasInitializedRef.current = true;
    }

    if (!isOpen) {
      // Reset initialization flag when modal closes
      hasInitializedRef.current = false;
    }

    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to prevent reset during typing, but use initialData directly when initializing

  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = e.target.value;
    handleInputChange("dateOfBirth", dateString);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange("phoneNumber", formatted);
  };

  const handleSsnLast4Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replaceAll(/\D/g, "").slice(0, 4);
    handleInputChange("ssnLast4", digitsOnly);
  };

  const runValidation = (): Record<keyof PatientFormData, string> => ({
    firstName: validateName(formData.firstName),
    lastName: validateName(formData.lastName),
    phoneNumber: validatePhoneNumber(formData.phoneNumber),
    dateOfBirth: validateDateOfBirth(formData.dateOfBirth),
    ssnLast4: validateSsnLast4(formData.ssnLast4),
    address: validateAddress(formData.address),
    addressLine2: "",
    city: validateCity(formData.city),
    state: validateState(formData.state),
    zip: validateZip(formData.zip),
  });

  const handleSubmit = async () => {
    const errors = runValidation();
    setValidationErrors(errors);

    const hasErrors = Object.values(errors).some((item) => item !== "");
    if (hasErrors) return;

    setError(null);
    setLoading(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      const conflictMessage = buildConflictMessage
        ? buildConflictMessage(formData, err)
        : null;
      if (conflictMessage) {
        setError(conflictMessage);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(defaultErrorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4 mb-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  placeholder="Enter first name"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.firstName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  placeholder="Enter last name"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.lastName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number *
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="1234567890"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.phoneNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.phoneNumber}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="dateOfBirth"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date of Birth *
                </label>
                <ImprovedDatePicker
                  id="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleDateChange}
                  className="w-full"
                  hasError={!!validationErrors.dateOfBirth}
                  required
                  placeholder="mm/dd/yyyy"
                />
                {validationErrors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.dateOfBirth}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="ssnLast4"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  social security number
                </label>
                <input
                  id="ssnLast4"
                  type="text"
                  inputMode="numeric"
                  value={formData.ssnLast4}
                  onChange={handleSsnLast4Change}
                  placeholder="1234"
                  title="Add last 4 digit of it"
                  maxLength={4}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.ssnLast4
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.ssnLast4 && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.ssnLast4}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              Patient Address
            </h3>

            <div className="space-y-4 border-gray-200">
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Address *
                </label>
                <input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="123 Main Street"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.address
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.address && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.address}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="addressLine2"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Address Line 2
                </label>
                <input
                  id="addressLine2"
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) =>
                    handleInputChange("addressLine2", e.target.value)
                  }
                  placeholder="Apartment, suite, unit, etc. (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="city"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    City *
                  </label>
                  <input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="New York"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.city
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {validationErrors.city && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.city}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="state"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    State *
                  </label>
                  <input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="NY or New York"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.state
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {validationErrors.state && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.state}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="zip"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    ZIP Code *
                  </label>
                  <input
                    id="zip"
                    type="text"
                    value={formData.zip}
                    onChange={(e) => handleInputChange("zip", e.target.value)}
                    placeholder="10001 or 10001-1234"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.zip
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {validationErrors.zip && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.zip}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-white border rounded-md hover:bg-brand-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            style={{ backgroundColor: "#4eb0e9" }}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {loadingLabel}
              </>
            ) : (
              primaryLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientFormModal;
